/**
 * Consent-gated analytics loader.
 *
 * Per client Decisions 7 & 8 (Rapport de pré-publication v2 §9):
 *   - GA4 script is NOT loaded at boot; it is injected dynamically only after
 *     the user clicks "Accept" in the cookie consent banner.
 *   - PostHog SDK is NOT loaded at boot; it is injected dynamically only after
 *     the user clicks "Accept".
 *   - On consent withdrawal, both are unloaded, their cookies are cleared
 *     and the PostHog identifier is reset.
 *   - `emergent-main.js` is preview-only and never loaded here.
 */

const GA_MEASUREMENT_ID = 'G-L9J9PD8T7L';
const POSTHOG_KEY = 'phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs';
const POSTHOG_HOST = 'https://us.i.posthog.com';

let gaLoaded = false;
let phLoaded = false;

/* ---------------------------------------------------------------- */
/* Google Analytics 4                                                */
/* ---------------------------------------------------------------- */

export const loadGA4 = () => {
  if (typeof window === 'undefined' || gaLoaded) return;

  // Initialize the queue + gtag stub
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    analytics_storage: 'granted',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
  window.gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true });

  // Inject the async gtag.js script
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  s.setAttribute('data-mgp-analytics', 'ga4');
  document.head.appendChild(s);

  gaLoaded = true;
};

export const unloadGA4 = () => {
  if (typeof window === 'undefined') return;
  // Remove the script tag so no further requests happen on next full load.
  document.querySelectorAll('script[data-mgp-analytics="ga4"]').forEach((el) => el.remove());
  // Tell the current in-page gtag instance to drop analytics.
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }
  // Delete GA cookies on the current domain.
  const host = window.location.hostname;
  const parts = host.split('.');
  const rootDomain = parts.length > 1 ? `.${parts.slice(-2).join('.')}` : host;
  const gaCookieNames = ['_ga', `_ga_${GA_MEASUREMENT_ID.replace('G-', '')}`, '_gid', '_gat'];
  gaCookieNames.forEach((name) => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${host};`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${rootDomain};`;
  });
  gaLoaded = false;
};

/* ---------------------------------------------------------------- */
/* PostHog                                                            */
/* ---------------------------------------------------------------- */

export const loadPostHog = () => {
  if (typeof window === 'undefined' || phLoaded) return;

  // Inline the PostHog stub-then-loader.
  // Adapted from the official PostHog snippet, kept minimal.
  (function (t, e) {
    let o; let n; let p; let r;
    if (!e.__SV) {
      window.posthog = e;
      e._i = [];
      e.init = function (i, s, a) {
        function g(target, method) {
          const parts = method.split('.');
          if (parts.length === 2) { target = target[parts[0]]; method = parts[1]; }
          target[method] = function () {
            target.push([method].concat(Array.prototype.slice.call(arguments, 0)));
          };
        }
        p = t.createElement('script');
        p.type = 'text/javascript';
        p.crossOrigin = 'anonymous';
        p.async = true;
        p.setAttribute('data-mgp-analytics', 'posthog');
        p.src = `${s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com')}/static/array.js`;
        r = t.getElementsByTagName('script')[0];
        r.parentNode.insertBefore(p, r);
        let u = e;
        if (a !== undefined) { u = e[a] = []; } else { a = 'posthog'; }
        u.people = u.people || [];
        u.toString = function (t2) {
          let s2 = 'posthog';
          if (a !== 'posthog') s2 += `.${a}`;
          if (!t2) s2 += ' (stub)';
          return s2;
        };
        u.people.toString = function () { return u.toString(1) + '.people (stub)'; };
        o = 'init capture identify alias reset opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing register register_once unregister get_distinct_id getFeatureFlag isFeatureEnabled reloadFeatureFlags onFeatureFlags people.set people.set_once'.split(' ');
        for (n = 0; n < o.length; n++) g(u, o[n]);
        e._i.push([i, s, a]);
      };
      e.__SV = 1;
    }
  }(document, window.posthog || []));

  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: 'identified_only',
    disable_session_recording: true,
    mask_all_text: true,
    mask_all_element_attributes: true,
    autocapture: false,
    capture_pageview: true,
    capture_pageleave: true,
  });

  phLoaded = true;
};

export const unloadPostHog = () => {
  if (typeof window === 'undefined') return;
  try {
    if (window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
      window.posthog.opt_out_capturing();
    }
    if (window.posthog && typeof window.posthog.reset === 'function') {
      window.posthog.reset(true);
    }
  } catch (e) { /* ignore */ }
  // Remove SDK script tag.
  document.querySelectorAll('script[data-mgp-analytics="posthog"]').forEach((el) => el.remove());
  // Clear PostHog cookies (ph_* pattern).
  document.cookie.split(';').forEach((cookie) => {
    const name = cookie.split('=')[0].trim();
    if (name.startsWith('ph_') || name.startsWith('_posthog')) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    }
  });
  phLoaded = false;
};

/* ---------------------------------------------------------------- */
/* High-level orchestrators                                           */
/* ---------------------------------------------------------------- */

export const enableAnalytics = () => {
  loadGA4();
  loadPostHog();
};

export const disableAnalytics = () => {
  unloadPostHog();
  unloadGA4();
};

export const resetTracking = () => {
  disableAnalytics();
};

/**
 * Legacy helper kept for compatibility with existing Header code.
 * Uses whatever gtag is currently loaded (no-op if analytics denied).
 */
export const trackEvent = (eventName, params = {}) => {
  if (typeof window === 'undefined') return;
  if (typeof window.gtag !== 'function') return;
  window.gtag('event', eventName, params);
};

export const trackProjectActClick = (source) => {
  trackEvent('project_act_cta_click', {
    event_category: 'project_act',
    event_label: source,
    source,
  });
};
